import { Modal } from "@seneca/senekit";

import useSimpleNavigationCallback from "seneca-common/utils/hooks/routing/useSimpleNavigationCallback";

import PathEndsWithModalJourneyWrapper from "features/common/paging/components/PathEndsWithModalJourneyWrapper";
import {
  InternalRedirectOnlyPathEndsWithRoute,
  PathEndsWithRoute,
  SignedOutPathEndsWithRoute
} from "features/common/routing/ends-with-routes";
import { ROUTES } from "routing/routes";

import ResetPassword from "../components/ResetPassword";
import ResetPasswordSignIn from "../components/ResetPasswordSignIn";
import SignIn from "../components/SignIn";
import SignUpAccountType from "../components/SignUpAccountType";
import SignUpFirstStage from "../components/SignUpFirstStage";
import SignUpStudentDetails from "../components/SignUpStudentDetails";
import SignUpTeacherDetails from "../components/SignUpTeacherDetails";
import SignUpUserDetails from "../components/SignUpUserDetails";
import SSOConfirmPasswordForm from "../components/SSOConfirmPasswordModal";
import useSignUpJourneyStateOnMount from "./hooks/useSignUpJourneyStateOnMount";
import { makeInternalLink, pathEndings } from "./utils";

const TOTAL_NUMBER_OF_RESET_PASSWORD_PAGES = 2;
const TOTAL_NUMBER_OF_SIGN_UP_PAGES = 3;

type Props = {
  accountType?: string;
  frontdoorModal?: boolean;
};

export default function GlobalSignUpModalJourney({
  accountType,
  frontdoorModal
}: Props) {
  const { backLocation, redirectAuthenticatedUserTo, search } =
    useSignUpJourneyStateOnMount();

  const onBackClick = useSimpleNavigationCallback(backLocation);

  const modalProps = {
    partOfJourney: true,
    fullscreenOnMobile: true
  };

  const closableModalProps = {
    ...modalProps,
    onCloseClick: frontdoorModal ? undefined : onBackClick,
    inContainer: frontdoorModal
  };

  return (
    // @ts-ignore
    <PathEndsWithModalJourneyWrapper
      backLink={backLocation}
      coverSiteHeader={!frontdoorModal}
      showOverlay={!frontdoorModal}
    >
      {/* Reset password */}
      <PathEndsWithRoute pathsEndingWith={pathEndings.resetPassword}>
        <Modal {...closableModalProps}>
          <ResetPassword
            linkToResetPasswordSignIn={makeInternalLink(
              pathEndings.resetPasswordSignIn
            )}
            linkToSignIn={makeInternalLink(pathEndings.signIn)}
          />
        </Modal>
      </PathEndsWithRoute>
      <InternalRedirectOnlyPathEndsWithRoute
        pathEndsWith={pathEndings.resetPasswordSignIn}
        redirectLink={makeInternalLink(pathEndings.resetPassword)}
      >
        <Modal {...closableModalProps}>
          <ResetPasswordSignIn
            pageNumber={2}
            totalNumberOfPages={TOTAL_NUMBER_OF_RESET_PASSWORD_PAGES}
            linkToPasswordReset={makeInternalLink(pathEndings.resetPassword)}
            redirectAuthenticatedUserTo={redirectAuthenticatedUserTo}
          />
        </Modal>
      </InternalRedirectOnlyPathEndsWithRoute>
      {/* Login */}
      <SignedOutPathEndsWithRoute
        pathEndsWith={pathEndings.signIn}
        preserveInitialSignedInState
        redirectAuthenticatedUserTo={redirectAuthenticatedUserTo}
      >
        <SignIn
          frontdoor={frontdoorModal}
          modalProps={closableModalProps}
          redirectAuthenticatedUserTo={redirectAuthenticatedUserTo}
          linkToPasswordReset={makeInternalLink(pathEndings.resetPassword)}
          linkToSignUp={makeInternalLink(pathEndings.signUp)}
        />
      </SignedOutPathEndsWithRoute>
      {/* Sign up */}
      <SignedOutPathEndsWithRoute
        pathEndsWith={pathEndings.signUp}
        redirectAuthenticatedUserTo={redirectAuthenticatedUserTo}
        preserveInitialSignedInState
      >
        <SignUpFirstStage
          frontdoor={frontdoorModal}
          modalProps={closableModalProps}
          linkToSignIn={makeInternalLink(pathEndings.signIn)}
          linkToNextPage={makeInternalLink(pathEndings.userDetails)}
          linkToNextPageSSO={makeInternalLink(pathEndings.userDetails)}
          accountType={accountType}
        />
      </SignedOutPathEndsWithRoute>
      <InternalRedirectOnlyPathEndsWithRoute
        pathEndsWith={pathEndings.userDetails}
        redirectLink={makeInternalLink(pathEndings.signUp)}
      >
        <Modal {...modalProps}>
          <SignUpUserDetails
            pageNumber={1}
            totalNumberOfPages={TOTAL_NUMBER_OF_SIGN_UP_PAGES}
            linkToNextPage={makeInternalLink(pathEndings.accountType)}
            search={search}
          />
        </Modal>
      </InternalRedirectOnlyPathEndsWithRoute>
      {/* TODO: change to InternalRedirectOnlyPathEndsWithRoute once hooked up
just making a signed out route to make it easier to test, as we don't link from anywhere in the app atm */}
      <SignedOutPathEndsWithRoute
        pathEndsWith={pathEndings.confirmPassword}
        // @ts-ignore
        redirectLink={makeInternalLink(pathEndings.signUp)}
      >
        <Modal {...modalProps}>
          <SSOConfirmPasswordForm />
        </Modal>
      </SignedOutPathEndsWithRoute>
      <InternalRedirectOnlyPathEndsWithRoute
        pathEndsWith={pathEndings.accountType}
        redirectLink={makeInternalLink(pathEndings.signUp)}
      >
        <Modal {...modalProps}>
          <SignUpAccountType
            pageNumber={2}
            totalNumberOfPages={TOTAL_NUMBER_OF_SIGN_UP_PAGES}
            linkToSignUpStudentDetails={makeInternalLink(
              pathEndings.studentDetails
            )}
            linkToSignUpTeacherDetails={makeInternalLink(
              pathEndings.teacherDetails
            )}
            search={search}
          />
        </Modal>
      </InternalRedirectOnlyPathEndsWithRoute>
      <InternalRedirectOnlyPathEndsWithRoute
        pathEndsWith={pathEndings.studentDetails}
        redirectLink={makeInternalLink(pathEndings.signUp)}
      >
        <Modal {...modalProps}>
          <SignUpStudentDetails
            pageNumber={3}
            totalNumberOfPages={TOTAL_NUMBER_OF_SIGN_UP_PAGES}
            linkToSignUpAccountType={makeInternalLink(pathEndings.accountType)}
            redirectAuthenticatedUserTo={redirectAuthenticatedUserTo}
          />
        </Modal>
      </InternalRedirectOnlyPathEndsWithRoute>
      <InternalRedirectOnlyPathEndsWithRoute
        pathEndsWith={pathEndings.teacherDetails}
        redirectLink={makeInternalLink(pathEndings.signUp)}
      >
        <Modal {...modalProps} overflowY="visible">
          <SignUpTeacherDetails
            pageNumber={3}
            totalNumberOfPages={TOTAL_NUMBER_OF_SIGN_UP_PAGES}
            linkToSignUpAccountType={makeInternalLink(pathEndings.accountType)}
            redirectAuthenticatedUserTo={ROUTES.TEACHER.buildPath({})}
          />
        </Modal>
      </InternalRedirectOnlyPathEndsWithRoute>
    </PathEndsWithModalJourneyWrapper>
  );
}
